// See aiOptInStore.md for the full contract and architecture.
import Vue from 'vue';
import { i18n } from '@/plugins/i18n';
import subscriptionService from '@/modules/subscription/services/subscriptionService';
import offeringService from '@/modules/purchasable/services/offeringService';
import logError from '@/utils/logError/logError';
import eventListenerService from '@/utils/eventCommunicationUtils/eventListenerService';
import {
  AI_OPT_IN_CURRENCY,
  AI_OPT_IN_DIALOG,
  AI_OPT_IN_ERRORS,
  AI_OPT_IN_EVENTS,
  AI_OPT_IN_FEATURE_FLAG,
  AI_OPT_IN_OFFERING_TYPE,
  AI_OPT_IN_PERMISSION,
  AI_OPT_IN_SKU,
  AI_OPT_IN_SOURCES,
} from '@/modules/bizai/constants/aiOptInConstants';

const ENDED_PURCHASE_STATES = ['canceled', 'expired'];
const MODAL_CLOSED_EVENT = 'modalClosed';
let optInRequestCounter = 0;

const nextOptInRequestId = () => {
  optInRequestCounter += 1;
  return `ai-opt-in-${Date.now()}-${optInRequestCounter}`;
};

const waitForDialogResult = (requestId) => new Promise((resolve) => {
  const onModalClosed = ({ name, result } = {}) => {
    if (name !== AI_OPT_IN_DIALOG || result?.requestId !== requestId) return;
    eventListenerService.removeListener(MODAL_CLOSED_EVENT, onModalClosed);
    resolve(Boolean(result.confirmed));
  };
  eventListenerService.addListener(MODAL_CLOSED_EVENT, onModalClosed);
});

const isSubscriptionActive = (subscription) => {
  if (!subscription?.is_active) return false;
  if (!ENDED_PURCHASE_STATES.includes(subscription.purchase_state)) return true;
  return new Date(subscription.expiration_date) > new Date();
};

const state = {
  isLoaded: false,
  isLoading: false,
  isSubmitting: false,
  subscription: null,
  offeringUid: null,
  lastError: null,
};

const getters = {
  requiresOptIn: (state, localGetters, rootState, rootGetters) => rootGetters['BusinessStore/hasFeature'](AI_OPT_IN_FEATURE_FLAG),
  isOptedIn: (state, localGetters) => !localGetters.requiresOptIn || Boolean(state.subscription),
  canOptIn: (state, localGetters, rootState, rootGetters) => rootGetters['StaffStore/hasPermission'](AI_OPT_IN_PERMISSION),
  isLoaded: (state) => state.isLoaded,
  isLoading: (state) => state.isLoading,
  isSubmitting: (state) => state.isSubmitting,
  subscription: (state) => state.subscription,
  lastError: (state) => state.lastError,
};

const mutations = {
  setLoaded(state, isLoaded) {
    state.isLoaded = Boolean(isLoaded);
  },
  setLoading(state, isLoading) {
    state.isLoading = Boolean(isLoading);
  },
  setSubmitting(state, isSubmitting) {
    state.isSubmitting = Boolean(isSubmitting);
  },
  setSubscription(state, subscription) {
    state.subscription = subscription || null;
  },
  setOfferingUid(state, offeringUid) {
    state.offeringUid = offeringUid || null;
  },
  setLastError(state, error) {
    state.lastError = error || null;
  },
};

const actions = {
  async fetchOptInState({ state, getters, commit }, { force = false } = {}) {
    if (!getters.requiresOptIn) return;
    if (state.isLoaded && !force) return;

    commit('setLoading', true);
    try {
      const subscriptions = await subscriptionService.getActiveSubscriptionsBySku(AI_OPT_IN_SKU);
      commit('setSubscription', subscriptions.find(isSubscriptionActive));
      commit('setLastError', null);
    } catch (error) {
      logError('Failed to fetch the BizAI opt-in subscription', error);
      commit('setSubscription', null);
      commit('setLastError', error);
    } finally {
      commit('setLoaded', true);
      commit('setLoading', false);
    }
  },

  async resolveOfferingUid({ state, commit }) {
    if (state.offeringUid) return state.offeringUid;

    const offerings = await offeringService.fetchOfferings({
      sku: AI_OPT_IN_SKU,
      type: AI_OPT_IN_OFFERING_TYPE,
      isActive: true,
    });
    const offering = Array.isArray(offerings) ? offerings[0] : null;
    if (!offering?.uid) throw new Error(AI_OPT_IN_ERRORS.OFFERING_NOT_FOUND);

    commit('setOfferingUid', offering.uid);
    return offering.uid;
  },

  async optIn({ state, commit, dispatch }, {
    entryPoint,
    feature,
    source = AI_OPT_IN_SOURCES.RAISE_HAND_DIALOG,
  } = {}) {
    commit('setSubmitting', true);
    try {
      const offeringUid = await dispatch('resolveOfferingUid');
      await subscriptionService.createV3Subscription({
        offering_uid: offeringUid,
        purchase_currency: AI_OPT_IN_CURRENCY,
      });
      await dispatch('fetchOptInState', { force: true });
      if (!state.subscription) throw new Error(AI_OPT_IN_ERRORS.SUBSCRIPTION_NOT_ACTIVE);

      Vue.prototype.$track(AI_OPT_IN_EVENTS.OPTED_IN, {
        entry_point: entryPoint,
        feature,
        source,
        subscription_uid: state.subscription.uid,
      });
    } catch (error) {
      logError('Failed to opt the business in to BizAI', error);
      Vue.prototype.$track(AI_OPT_IN_EVENTS.OPT_IN_FAILED, {
        entry_point: entryPoint,
        feature,
        error: error?.message,
      });
      throw error;
    } finally {
      commit('setSubmitting', false);
    }
  },

  async requestOptIn({ getters, dispatch }, {
    entryPoint,
    feature,
    source = AI_OPT_IN_SOURCES.RAISE_HAND_DIALOG,
  } = {}) {
    if (!getters.requiresOptIn) return { optedIn: true, confirmed: true };
    await dispatch('fetchOptInState');
    if (getters.isOptedIn) return { optedIn: true, confirmed: true };

    const requestId = nextOptInRequestId();
    const dialogResult = waitForDialogResult(requestId);
    dispatch('ModalStore/openModal', {
      name: AI_OPT_IN_DIALOG,
      payload: [{ entryPoint, feature, requestId }],
    }, { root: true });
    const confirmed = await dialogResult;
    if (!confirmed) return { optedIn: false, confirmed: false };

    try {
      await dispatch('optIn', { entryPoint, feature, source });
      return { optedIn: true, confirmed: true };
    } catch (error) {
      dispatch('ToastStore/setToastConfig', {
        value: true,
        type: 'attention',
        bodyText: i18n.t('bizai.opt_in.error'),
        timeout: 3000,
      }, { root: true });
      return { optedIn: false, confirmed: true, error: error?.message };
    }
  },

  async optOut({ state, commit, dispatch }, { source = AI_OPT_IN_SOURCES.SETTINGS_PAGE } = {}) {
    if (!state.subscription) return;

    const subscriptionUid = state.subscription.uid;
    commit('setSubmitting', true);
    try {
      await subscriptionService.cancelV3Subscription(subscriptionUid);
      await dispatch('fetchOptInState', { force: true });
      if (state.subscription?.uid === subscriptionUid) commit('setSubscription', null);
      Vue.prototype.$track(AI_OPT_IN_EVENTS.OPTED_OUT, { source, subscription_uid: subscriptionUid });
    } catch (error) {
      logError('Failed to opt the business out of BizAI', error);
      throw error;
    } finally {
      commit('setSubmitting', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
